import Vue from 'vue';
import Router from 'vue-router';

import { Api } from '../api';
import { AppModel } from '../models';
import { Tracking } from '../utils';
import {
    AvailabilitiesIntro, AvailabilitiesByMonth, AvailabilitiesByCampsite,
    Dashboard, Login, Bookings, BookingDetail, BookingNew, CampsiteImages,
    Portals, Profile, Seasons, CampsiteCalendar, Campsites, Persons, Extras, Settings, Translations,
    NotFound, CancellationTerms, PmsAccommodations,
} from '../views/pages';
import { i18nValidation } from '../localize';

Vue.use(Router);

const defaultLanguageProp = (route) => {
    return {
        lang: route.params.lang || AppModel.lang,
    };
};

let router = new Router({
    mode: 'history',
    linkActiveClass: 'is-active',
    routes: [
        {
            name: 'slash',
            path: '/',
            beforeEnter(to, from, next) {
                i18nValidation.locale = AppModel.lang;

                next(`/${AppModel.lang}`);
            },
        },
        {
            name: 'home',
            path: '/:lang([a-z]{2})/',
            component: Dashboard,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'new-booking',
            path: '/:lang([a-z]{2})/bookings/new',
            component: BookingNew,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'bookings',
            path: '/:lang([a-z]{2})/bookings',
            component: Bookings,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'booking-detail-redirect',
            path: '/booking/:bookingId',
            beforeEnter(to, from, next) {
                next(`/${AppModel.lang}/booking/${to.params.bookingId}`);
            },
        },
        {
            name: 'booking-detail',
            path: '/:lang([a-z]{2})/booking/:bookingId',
            component: BookingDetail,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'availabilities-redirect',
            path: '/:lang([a-z]{2})/availabilities',
            beforeEnter(to, from, next) {
                next(`/${to.params.lang}/availabilities/year/${new Date().getFullYear()}`);
            },
            props: defaultLanguageProp,
        },
        {
            name: 'availabilities-index',
            path: '/:lang([a-z]{2})/availabilities/year/:year',
            props: (route) => {
                return {
                    year: Number(route.params.year),
                    lang: route.params.lang || AppModel.lang,
                };
            },
            component: AvailabilitiesIntro,
            beforeEnter: Api.requireAuth.bind(Api)
        },
        {
            name: 'availabilities-by-month',
            path: '/:lang([a-z]{2})/availabilities/month/:year/:month',
            props: (route) => {
                return {
                    year: Number(route.params.year),
                    month: Number(route.params.month),
                    lang: route.params.lang || AppModel.lang,
                };
            },
            component: AvailabilitiesByMonth,
            beforeEnter: Api.requireAuth.bind(Api)
        },
        {
            name: 'availabilities-by-campsite',
            path: '/:lang([a-z]{2})/availabilities/campsite/:campsiteId',
            props: (route) => {
                return {
                    campsiteId: Number(route.params.campsiteId),
                    lang: route.params.lang || AppModel.lang,
                };
            },
            component: AvailabilitiesByCampsite,
            beforeEnter: Api.requireAuth.bind(Api)
        },
        {
            name: 'portals',
            path: '/:lang([a-z]{2})/portals',
            component: Portals,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'seasons',
            path: '/:lang([a-z]{2})/seasons',
            component: Seasons,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'dayprices',
            path: '/:lang([a-z]{2})/dayprices',
            component: CampsiteCalendar,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'cancellation-terms',
            path: '/:lang([a-z]{2})/cancellation-terms',
            component: CancellationTerms,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'campsites',
            path: '/:lang([a-z]{2})/campsites',
            component: Campsites,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'pms-accommodations',
            path: '/:lang([a-z]{2})/pms-accommodations',
            component: PmsAccommodations,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'pms-accommodations-new',
            path: '/:lang([a-z]{2})/pms-accommodations/new',
            component: () => import('../views/pages/pms-accommodations/pms-accommodation-detail.vue'),
            beforeEnter: Api.requireAuth.bind(Api),
            props: (route) => ({ ...defaultLanguageProp(route), id: null }),
        },
        {
            name: 'pms-accommodations-detail',
            path: '/:lang([a-z]{2})/pms-accommodations/:id',
            component: () => import('../views/pages/pms-accommodations/pms-accommodation-detail.vue'),
            beforeEnter: Api.requireAuth.bind(Api),
            props: (route) => ({ ...defaultLanguageProp(route), id: Number(route.params.id) }),
        },
        {
            name: 'campsite-images',
            path: '/:lang([a-z]{2})/campsites/:campsiteId',
            component: CampsiteImages,
            beforeEnter: Api.requireAuth.bind(Api),
            props: (route) => {
                return {
                    campsiteId: Number(route.params.campsiteId),
                    lang: route.params.lang || AppModel.lang,
                };
            },
        },
        {
            name: 'persons',
            path: '/:lang([a-z]{2})/persons',
            component: Persons,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        // {
        //     name: 'payments',
        //     path: '/:lang([a-z]{2})/payments',
        //     component: Payments,
        //     beforeEnter: Api.requireAuth.bind(Api),
        //     props: defaultLanguageProp,
        // },
        // {
        //     name: 'invoices',
        //     path: '/:lang([a-z]{2})/invoices',
        //     component: Invoices,
        //     beforeEnter: Api.requireAuth.bind(Api),
        //     props: defaultLanguageProp,
        // },
        {
            name: 'extras',
            path: '/:lang([a-z]{2})/extras',
            component: Extras,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'translations',
            path: '/:lang([a-z]{2})/translations',
            component: Translations,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'settings',
            path: '/:lang([a-z]{2})/settings',
            component: Settings,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'profile',
            path: '/:lang([a-z]{2})/profile',
            component: Profile,
            beforeEnter: Api.requireAuth.bind(Api),
            props: defaultLanguageProp,
        },
        {
            name: 'login',
            path: '/login',
            component: Login,
            beforeEnter(to, from, next) {
                if(Api.user.loggedIn()) {
                    next('/');
                }

                next();
            }
        },
        {
            name: 'logout',
            path: '/logout',
            beforeEnter(to, from, next) {
                Api.user.logout();
                next('/login');
            },
        },
        {
            name: 'change-campground',
            path: '/:lang([a-z]{2})/set-campground/:campground',
            props: true,
            beforeEnter(to, from, next) {
                AppModel.setActiveCampground(to.params.campground);
                window.location.reload();
            },
        },
        {
            name: 'not-found',
            path: '/*',
            component: NotFound,
            props: defaultLanguageProp
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        return { x: 0, y: 0 };
    }
});

router.afterEach((to, from) => {
    setTimeout(() => {
        Tracking.trackPageView();
    }, 0);
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js